'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Search, Shield } from 'lucide-react';

const navLinks = [
  { label: 'Browse Experts', href: '/expert-browse-page' },
  { label: 'Post a Query',   href: '/new-query-expert-match' },
  { label: 'Enrol as Expert', href: '/expert-onboarding-wizard' },
  { label: 'My Engagements', href: '/buyer/engagements' },
  { label: 'My Enquiries', href: '/expert/engagements' },
];

export default function AppNav() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-40 border-b border-border bg-background/90 backdrop-blur-md">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 2xl:px-16 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href="/" className="flex items-center gap-2.5 group">
            <AppLogo size={32} />
            <span className="font-heading text-xl font-semibold text-foreground tracking-tight">
              Expert<span className="text-primary">Vault</span>
            </span>
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
          {navLinks?.map((link) => (
            <Link
              key={`nav-${link?.href}`}
              href={link?.href}
              className={`px-4 py-2 rounded text-sm transition-colors duration-150 ${
                pathname === link?.href
                  ? 'text-primary bg-primary/8 font-medium' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            href="/admin-panel"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors ${
              pathname === '/admin-panel' ?'text-secondary bg-secondary/10' :'text-muted-foreground hover:text-secondary'
            }`}
            aria-label="Admin panel"
          >
            <Shield size={14} />
            <span className="font-mono-data">Admin</span>
          </Link>
          <Link
            href="/expert-browse-page"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all duration-150"
          >
            <Search size={14} />
            Find Expert
          </Link>
          <Link
            href="/new-query-expert-match"
            className="px-4 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all duration-150"
          >
            Post Query
          </Link>
        </div>

        <button
          className="md:hidden p-2 rounded text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-card animate-fade-in">
          <nav className="px-4 py-3 flex flex-col gap-1" aria-label="Mobile navigation">
            {navLinks?.map((link) => (
              <Link
                key={`mobile-nav-${link?.href}`}
                href={link?.href}
                className={`px-3 py-2.5 rounded text-sm transition-colors ${
                  pathname === link?.href
                    ? 'text-primary bg-primary/8 font-medium' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                onClick={() => setMobileOpen(false)}
              >
                {link?.label}
              </Link>
            ))}
            <Link
              href="/admin-panel"
              className="px-3 py-2.5 rounded text-sm text-muted-foreground hover:text-secondary transition-colors flex items-center gap-2"
              onClick={() => setMobileOpen(false)}
            >
              <Shield size={14} />
              Admin Panel
            </Link>
            <div className="pt-2 mt-1 border-t border-border flex flex-col gap-2">
              <Link
                href="/new-query-expert-match"
                className="px-4 py-2.5 rounded bg-primary text-primary-foreground text-sm font-medium text-center active:scale-95 transition-all"
                onClick={() => setMobileOpen(false)}
              >
                Post Query
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}